import { ReactNode, useEffect, useState } from "react";

type Breakpoints = {
    base: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
};

type RowMasonryProps<T> = {
    items: T[];
    getKey: (item: T, index: number) => string | number;
    renderItem: (item: T, index: number) => ReactNode;
    columns?: Breakpoints;
    gapClassName?: string;
};

const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280 } as const;

const resolveColumnCount = (cfg: Breakpoints, width: number) => {
    if (width >= BREAKPOINTS.xl && cfg.xl !== undefined) return cfg.xl;
    if (width >= BREAKPOINTS.lg && cfg.lg !== undefined) return cfg.lg;
    if (width >= BREAKPOINTS.md && cfg.md !== undefined) return cfg.md;
    if (width >= BREAKPOINTS.sm && cfg.sm !== undefined) return cfg.sm;
    return cfg.base;
};

export function RowMasonry<T>({
    items,
    getKey,
    renderItem,
    columns = { base: 1, md: 2, lg: 3 },
    gapClassName = "gap-4",
}: RowMasonryProps<T>) {
    // Depend on the individual breakpoint scalars so callers can pass an
    // inline `columns` object literal without re-binding the resize listener
    // on every render.
    const { base, sm, md, lg, xl } = columns;

    const [colCount, setColCount] = useState(() =>
        typeof window === "undefined"
            ? base
            : resolveColumnCount({ base, sm, md, lg, xl }, window.innerWidth),
    );

    useEffect(() => {
        const update = () =>
            setColCount(resolveColumnCount({ base, sm, md, lg, xl }, window.innerWidth));
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [base, sm, md, lg, xl]);

    const buckets: { item: T; index: number }[][] = Array.from({ length: colCount }, () => []);
    items.forEach((item, index) => {
        buckets[index % colCount].push({ item, index });
    });

    return (
        <div className={`flex w-full items-start ${gapClassName}`}>
            {buckets.map((bucket, colIdx) => (
                <div key={colIdx} className={`flex flex-1 flex-col ${gapClassName}`}>
                    {bucket.map(({ item, index }) => (
                        <div key={getKey(item, index)}>{renderItem(item, index)}</div>
                    ))}
                </div>
            ))}
        </div>
    );
}
